import { lazy, Suspense, type ReactNode } from "react";
import { Adjuntos, Alerta, BarraApilada, Brujula, Button, Card, Carrusel, Casilla, Dialog, Fachada, GrillaPisos, MapaDireccion, Row, Spinner, Stack } from "@/components";
import { TIPOS_MAQUETA } from "@/lib/adjuntos";
import { detalleCelda } from "@/lib/edificio";
import { formatM2, nombrePiso, nombreUnidad } from "@/lib/unidades";
import { ETIQUETA_ESTADO_UNIDAD } from "@/lib/ventas";
import { CierreProyecto, EncabezadoUnidades, PortadaProyecto, RelatoProyecto } from "./Presentacion";
import { useProyectoTab } from "./useProyectoTab";

// three.js pesa: la maqueta se baja recién al abrir la vista 3D.
const Maqueta3D = lazy(() => import("@/components/Maqueta3D"));

/** Un botón de filtro o de vista, en píldora: terracota si está elegido (la línea de la presentación). */
function Opcion({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: string }) {
  return (
    <Button
      variante="secundario"
      tamanio="sm"
      aria-pressed={activa}
      onClick={onClick}
      className={`shrink-0 rounded-full px-3.5 font-normal ${activa ? "border-marca bg-marca text-white hover:bg-marca" : "border-carbon/20 bg-transparent text-carbon hover:bg-carbon/5"}`}
    >
      {children}
    </Button>
  );
}

/** Antetítulo chico en mayúsculas espaciadas, como en la carpeta. */
function Rotulo({ children, as: Tag = "h3" }: { children: ReactNode; as?: "h2" | "h3" | "p" }) {
  return <Tag className="text-[0.6875rem] font-semibold uppercase tracking-[0.22em] text-marca">{children}</Tag>;
}

/** Un dato de la unidad en la ficha, en píldora. */
function Chip({ children, token }: { children: ReactNode; token?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-carbon/15 px-3 py-1 text-chico text-carbon">
      {token && <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: `var(${token})` }} />}
      {children}
    </span>
  );
}

/**
 * Proyecto (fiduciante y admin; rediseño de Mati, 2026-11-02, y con la línea de la carpeta comercial, 2026-11-03):
 * cuántos quedan, filtros fijos arriba, el edificio (frente o maqueta 3D) al costado y la grilla por piso; tocar una
 * unidad abre su ficha con las imágenes grandes.
 */
export function ProyectoTab() {
  const t = useProyectoTab();
  const u = t.elegida;

  return (
    <Stack espacio={6}>
      {t.presentacion ? (
        <>
          <PortadaProyecto p={t.presentacion} disponibles={t.disponiblesPortada} />
          <RelatoProyecto p={t.presentacion} planta={t.planta} onElegirPlanta={t.elegirPlanta} espacio={t.espacio} onElegirEspacio={t.elegirEspacio} />
        </>
      ) : (
        <Stack espacio={1}>
          <h1 className="text-pantalla font-light tracking-[-0.03em]">Proyecto</h1>
          {t.nombre && <h2 className="text-cuerpo text-texto-suave">{t.nombre}</h2>}
        </Stack>
      )}

      <section id="unidades" aria-label="Elegí tu unidad" className="flex scroll-mt-16 flex-col gap-6">
        {t.presentacion && <EncabezadoUnidades />}
        {t.cargando && (
          <div className="grid place-items-center py-6 text-marca">
            <Spinner />
          </div>
        )}
        {t.error && (
          <Stack espacio={3}>
            <Alerta texto={t.error} />
            <div>
              <Button variante="secundario" onClick={t.reintentar}>
                Reintentar
              </Button>
            </div>
          </Stack>
        )}
        {!t.cargando && !t.error && !t.hayUnidades && <p className="text-texto-suave">Todavía no hay unidades cargadas.</p>}

        {t.hayUnidades && (
          <>
            <section aria-label="Disponibilidad" className="flex flex-col gap-3 text-carbon">
              <p className="flex flex-wrap items-baseline gap-3">
                <span className="text-[clamp(3.5rem,8vw,5.5rem)] font-extralight leading-none tracking-[-0.045em] tabular-nums text-marca">{t.disponibilidad.disponibles}</span>
                <span className="text-subtitulo font-light text-carbon/80">{t.disponibilidad.detalle}</span>
              </p>
              <BarraApilada etiqueta="Unidades por estado" segmentos={t.disponibilidad.segmentos} />
              {t.otrosTipos.length > 0 && <p className="text-chico text-carbon/60">{t.otrosTipos.map((r) => `${r.titulo}: ${r.detalle}`).join(" · ")}</p>}
            </section>

            <div role="region" aria-label="Filtros" className="sticky top-14 z-10 -mx-4 border-y border-carbon/10 bg-fondo/90 px-4 py-3 backdrop-blur-md md:mx-0 md:rounded-full md:border md:px-4">
              <Row espacio={4} envolver className="gap-y-2">
                {t.opcionesAmbientes.length > 1 && (
                  <Row espacio={1} role="group" aria-label="Ambientes" className="max-w-full overflow-x-auto [scrollbar-width:none]">
                    <Opcion activa={t.filtro.ambientes === null} onClick={() => t.elegirAmbientes(null)}>
                      Todos
                    </Opcion>
                    {t.opcionesAmbientes.map((o) => (
                      <Opcion key={o.valor} activa={t.filtro.ambientes === o.valor} onClick={() => t.elegirAmbientes(o.valor)}>
                        {o.etiqueta}
                      </Opcion>
                    ))}
                  </Row>
                )}
                {t.hayLados && (
                  <Row espacio={1} role="group" aria-label="Ubicación">
                    <Opcion activa={t.filtro.lado === null} onClick={() => t.elegirLado(null)}>
                      Todas
                    </Opcion>
                    <Opcion activa={t.filtro.lado === "frente"} onClick={() => t.elegirLado("frente")}>
                      Al frente
                    </Opcion>
                    <Opcion activa={t.filtro.lado === "contrafrente"} onClick={() => t.elegirLado("contrafrente")}>
                      Contrafrente
                    </Opcion>
                  </Row>
                )}
                <Casilla etiqueta="Sólo disponibles" marcado={t.filtro.soloDisponibles} onCambio={t.elegirSoloDisponibles} className="text-chico text-carbon" />
                <Row espacio={1} className="ml-auto text-chico text-carbon/60">
                  <span role="status">
                    <strong className="font-semibold tabular-nums text-carbon">{t.coinciden}</strong> {t.coinciden === 1 ? "coincide" : "coinciden"}
                  </span>
                  {t.hayFiltro && (
                    <Button variante="fantasma" tamanio="sm" onClick={t.limpiarFiltro} className="rounded-full text-marca hover:bg-marca/10">
                      Limpiar
                    </Button>
                  )}
                </Row>
              </Row>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] md:items-start md:gap-8">
              <Card relleno="ninguno" className="border-0 bg-crema p-5 text-carbon shadow-none md:sticky md:top-36">
                <Stack espacio={4}>
                  <Row justificar="entre" className="gap-2">
                    <h2 className="text-[1.5rem] font-light tracking-tight">El edificio</h2>
                    <Row espacio={1} aria-label="Vista">
                      <Opcion activa={t.vista === "plano"} onClick={() => t.elegirVista("plano")}>
                        Plano
                      </Opcion>
                      <Opcion activa={t.vista === "maqueta"} onClick={() => t.elegirVista("maqueta")}>
                        Maqueta 3D
                      </Opcion>
                    </Row>
                  </Row>
                  {t.vista === "plano" && (
                    <>
                      <Fachada etiqueta="Frente del edificio" pisos={t.fachada} elegido={t.pisoFachada} onElegir={t.irAPiso} />
                      <p className="text-chico text-carbon/60">Tocá un piso para ir a sus unidades. Lo que no coincide con los filtros se ve gris.</p>
                    </>
                  )}
                  {t.vista === "maqueta" && (
                    <>
                      <Row espacio={1} envolver aria-label="Pisos" className="gap-y-1">
                        <Opcion activa={t.pisoElegido === null} onClick={() => t.elegirPiso(null)}>
                          Edificio
                        </Opcion>
                        {t.pisos.map((p) => (
                          <Opcion key={p.piso} activa={t.pisoElegido === p.piso} onClick={() => t.elegirPiso(p.piso)}>
                            {p.etiqueta}
                          </Opcion>
                        ))}
                      </Row>
                      <Suspense
                        fallback={
                          <div className="grid h-80 place-items-center rounded-control bg-white/60 text-marca">
                            <Spinner />
                          </div>
                        }
                      >
                        <Maqueta3D etiqueta="Maqueta del edificio" unidades={t.enMaqueta} modeloUrl={t.modeloUrl} esquema={t.esquema} pisoElegido={t.pisoElegido} elegida={u?.id ?? null} onElegir={t.elegir} />
                      </Suspense>
                      <p className="text-chico text-carbon/60">
                        Girá la maqueta arrastrando y tocá una unidad para ver su ficha.
                        {t.esquematica && " Es esquemática, armada con las unidades cargadas: cada caja es una unidad, del tamaño aproximado de su superficie."}
                        {t.esquematica && t.esAdmin && " Cuando subas la maqueta del edificio (.glb), aparece en su lugar."}
                      </p>
                    </>
                  )}
                  <Row espacio={3} envolver aria-label="Referencias" className="gap-y-1.5 border-t border-carbon/10 pt-3">
                    {t.leyenda.map((e) => (
                      <span key={e.clave} className="inline-flex items-center gap-1.5 text-[0.6875rem] uppercase tracking-[0.12em] text-carbon/70">
                        <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: `var(${e.token})` }} />
                        {e.etiqueta}
                      </span>
                    ))}
                  </Row>
                </Stack>
              </Card>

              <section aria-label="Unidades" className="min-w-0">
                {t.grilla.length > 0 ? (
                  <GrillaPisos etiqueta="Unidades por piso" pisos={t.grilla} elegida={u?.id ?? null} onElegir={t.elegir} />
                ) : (
                  <Stack espacio={3} className="items-center rounded-tarjeta bg-crema py-12 text-center text-carbon/70">
                    <p className="text-subtitulo font-light">Ninguna unidad coincide con estos filtros.</p>
                    <Opcion activa onClick={t.limpiarFiltro}>
                      Limpiar filtros
                    </Opcion>
                  </Stack>
                )}
              </section>
            </div>

            {t.esAdmin && (
              <Card className="border-carbon/10 shadow-none">
                <Stack espacio={3}>
                  <Rotulo as="p">Sólo el admin</Rotulo>
                  <h2 className="text-[1.5rem] font-light tracking-tight">Maqueta 3D del edificio</h2>
                  <Adjuntos
                    etiqueta="Maqueta del edificio (.glb)"
                    adjuntos={t.archivoMaqueta}
                    rutaDescarga={t.rutaDescarga}
                    puedeEditar
                    tipos={TIPOS_MAQUETA}
                    onSubir={t.subirMaqueta}
                    onBorrar={t.borrarMaqueta}
                    subiendo={t.subiendoMaqueta}
                    error={t.errorMaqueta}
                  />
                  {t.puedeArmarEjemplo && (
                    <Stack espacio={2}>
                      <p className="text-chico text-texto-suave">
                        ¿Todavía no tenés los planos? Armá una de ejemplo con tus unidades: se inventa la planta de cada una con sus m² y ambientes, la
                        maqueta con muros, puertas, ventanales y muebles, y a cada depto se le suben un plano y una vista desde arriba. Todo queda con
                        nombre “ejemplo-…” para que lo reemplaces por lo real.
                      </p>
                      <div>
                        <Button variante="secundario" cargando={t.armandoEjemplo} onClick={t.armarEjemplo} className="rounded-full">
                          {t.armandoEjemplo ? "Armando la maqueta…" : t.rehaceEjemplo ? "Volver a armar la maqueta de ejemplo" : "Armar maqueta de ejemplo"}
                        </Button>
                      </div>
                      <Alerta texto={t.errorEjemplo} />
                    </Stack>
                  )}
                  {t.avisoEjemplo && (
                    <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
                      {t.avisoEjemplo}
                    </p>
                  )}
                </Stack>
              </Card>
            )}
          </>
        )}
      </section>

      {t.presentacion && <CierreProyecto p={t.presentacion} onCompartir={t.compartir} avisoCompartir={t.avisoCompartir} />}

      {t.direccion && !t.presentacion && (
        <Card>
          <Stack espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Dónde queda</h2>
            <MapaDireccion direccion={t.direccion} />
          </Stack>
        </Card>
      )}

      <Dialog abierto={u !== null} onCambio={(abierto) => !abierto && t.cerrarFicha()} titulo={u ? nombreUnidad(u) : ""}>
        {u && (
          <Stack espacio={4} onTouchStart={t.alTocarFicha} onTouchEnd={t.alSoltarFicha} className="text-carbon">
            <Row justificar="entre">
              <Button variante="secundario" tamanio="sm" aria-label="Unidad anterior" disabled={!t.hayAnterior} onClick={t.anterior} className="rounded-full border-carbon/20 bg-transparent">
                ‹ Anterior
              </Button>
              <span className="text-[0.6875rem] uppercase tracking-[0.16em] tabular-nums text-carbon/60">{t.posicionFicha}</span>
              <Button variante="secundario" tamanio="sm" aria-label="Unidad siguiente" disabled={!t.haySiguiente} onClick={t.siguiente} className="rounded-full border-carbon/20 bg-transparent">
                Siguiente ›
              </Button>
            </Row>
            {t.fotos.length > 0 ? (
              <Carrusel key={u.id} etiqueta={`Imágenes de ${nombreUnidad(u)}`} fotos={t.fotos} />
            ) : (
              <p className="rounded-tarjeta bg-crema px-4 py-8 text-center text-chico text-carbon/60">Todavía no hay plano ni renders de esta unidad.</p>
            )}
            <Row espacio={2} envolver>
              <Chip token={t.tokenElegida ?? undefined}>{ETIQUETA_ESTADO_UNIDAD[u.estado_venta]}</Chip>
              <Chip>{nombrePiso(u.piso)}</Chip>
              <Chip>{detalleCelda(u)}</Chip>
              {u.frente !== null && <Chip>{u.frente ? "Al frente" : "Contrafrente"}</Chip>}
            </Row>
            {t.tieneRecorrido && (
              <Button onClick={t.recorrer} anchoCompleto className="h-12 rounded-full bg-marca text-white hover:bg-marca-hover">
                Recorrerlo por dentro
              </Button>
            )}
            {t.superficie && (
              <Stack espacio={2} className="border-t border-carbon/10 pt-4">
                <Rotulo>Superficie</Rotulo>
                <p className="flex items-baseline gap-2">
                  <span className="text-[2.75rem] font-extralight leading-none tracking-[-0.04em] tabular-nums">{formatM2(u.sup_total_m2)}</span>
                  <span className="text-chico text-carbon/60">totales</span>
                </p>
                <BarraApilada etiqueta="Superficie" segmentos={t.superficie} />
              </Stack>
            )}
            {t.sol && (
              <Stack espacio={2} className="border-t border-carbon/10 pt-4">
                <Rotulo>Orientación y sol</Rotulo>
                <Row espacio={3} className="rounded-tarjeta bg-crema p-4">
                  <Brujula grados={u.mira_a ?? 0} />
                  <p className="text-chico text-carbon/80">
                    <strong className="block text-subtitulo font-light text-carbon">{t.sol.titulo}</strong>
                    {t.sol.texto}
                  </p>
                </Row>
              </Stack>
            )}
            {u.orientacion && !t.sol && <p className="text-chico text-carbon/60">Orientación: {u.orientacion}</p>}
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
